import React, { Component } from "react";
import { Link } from "react-router-dom";
import SelectField from "components/forms/SelectField";
import Modal from "components/Modal";
import Form, { FormStatus } from "components/forms/Form";
import { Design, DesignSourceType } from "services/entities/designs";
import { ValidationGroup, Validators } from "util/validation";
import TextField from "components/forms/TextField";
import { Button, MenuItem } from "@material-ui/core";
import ProgressSpinner from "components/ProgressSpinner";
import { Space, SpaceStatus } from "services/entities/spaces";
import { loadSpaces } from "services/spaces";
import { createDesign } from "services/designs";
import "./index.scss";
import { CancellablePromise, WrappedCancellablePromise } from "util/promises";
import { handleError, ValueError } from "util/errors";
import NextRouteURLs from "NextRouteURLs";

interface CreateDesignModalProps {
  open: boolean;
  onClosing?: () => void;
  onCreated?: (design: Design) => void;
}

interface CreateDesignRequest {
  space: string;
  name: string;
  sourceType: DesignSourceType;
}

interface CreateDesignModalState {
  status: FormStatus;
  model: CreateDesignRequest;
  spaces: Space[];
}

export default class CreateDesignModal extends Component<
  CreateDesignModalProps,
  CreateDesignModalState
> {
  private readonly validation: ValidationGroup;
  private loadSpacesPromise: CancellablePromise<Space[]> | null = null;

  state: CreateDesignModalState = {
    status: FormStatus.None,
    model: {
      space: "",
      name: "",
      sourceType: DesignSourceType.File,
    },
    spaces: [],
  };

  constructor(props: CreateDesignModalProps) {
    super(props);
    this.validation = new ValidationGroup(this.state.model);
    this.validation.addField("space", {
      required: Validators.required,
    });
    this.validation.addField("name", {
      required: Validators.required,
      minLength: Validators.minLength(5),
    });
    this.validation.addField("sourceType", {
      required: Validators.required,
    });
  }

  componentDidMount(): void {
    this.loadSpacesPromise = new WrappedCancellablePromise(loadSpaces());
    this.loadSpacesPromise.then(
      (spaces) => {
        spaces = spaces.filter(
          (sp) =>
            (sp.status === SpaceStatus.Available ||
              sp.status === SpaceStatus.Preview) &&
            sp.designerEnabled
        );
        this.loadSpacesPromise = null;
        if (spaces.length === 1) {
          const model = this.state.model;
          model.space = spaces[0].code;
          this.setState({ model: model, spaces: spaces });
          this.validation.validateField("model");
        } else {
          this.setState({ spaces: spaces });
        }
      },
      () => {
        handleError("Failed loading spaces");
        this.loadSpacesPromise = null;
      }
    );
  }

  componentWillUnmount(): void {
    if (this.loadSpacesPromise) {
      this.loadSpacesPromise.cancel();
      this.loadSpacesPromise = null;
    }
  }

  onSubmit = () => {
    this.setState({ status: FormStatus.Submitting });
    createDesign(this.state.model).then(
      (design) => {
        this.setState({ status: FormStatus.Success });
        if (this.props.onCreated) {
          this.props.onCreated(design);
        }
      },
      (err) => {
        // must set state first - so fields are rendered before adding errors
        this.setState({ status: FormStatus.Failure });
        if (err instanceof ValueError) {
          if (err.errors.length > 0) {
            handleError("Create design failed", err);
          }
          for (const key of Object.keys(err.fieldErrors)) {
            for (const fieldError of err.fieldErrors[key]) {
              this.validation.addFieldError(key, fieldError);
            }
          }
        } else {
          handleError("Create design failed", err);
        }
      }
    );
  };

  onFieldInput = () => {
    this.setState({ status: FormStatus.None });
  };

  resetState() {
    let spaceCode = "";
    const spaces = this.state.spaces;
    if (spaces && spaces.length === 1) {
      spaceCode = (spaces[0] as Space).code;
    }
    const model = {
      space: spaceCode,
      name: "",
      sourceType: DesignSourceType.File,
    };
    this.setState({
      status: FormStatus.None,
      model: model,
    });
    this.validation.reset(model);
  }

  onOpening = () => {
    this.resetState();
  };

  render() {
    const disabled =
      this.state.status === FormStatus.Submitting ||
      this.state.status === FormStatus.Success;
    const model = this.state.model;
    const spaces: Space[] = this.state.spaces;
    return (
      <Modal
        className="new-design"
        open={this.props.open}
        onClosing={this.props.onClosing}
        onOpening={this.onOpening}
        closable={!disabled}
      >
        {!disabled && (
          <Form onSubmit={this.onSubmit} validationGroup={this.validation}>
            <h2>Create a design</h2>
            {spaces.length > 1 && (
              <SelectField
                label="Select a space"
                name="space"
                model={model}
                validationGroup={this.validation}
                disabled={disabled}
                onChange={this.onFieldInput}
                validationMessages={{ required: "Space is required" }}
              >
                {spaces.map((sp) => (
                  <MenuItem key={sp.code} value={sp.code}>
                    {sp.name}
                  </MenuItem>
                ))}
              </SelectField>
            )}
            <TextField
              name="name"
              label="Name your design"
              type="text"
              required={true}
              validationGroup={this.validation}
              model={model}
              validationMessages={{
                required: "Name is required",
                minLength: "Name must be at least 5 characters",
              }}
              disabled={disabled}
              onInput={this.onFieldInput}
            />
            <Button variant="contained" type="submit" disabled={disabled}>
              Create
            </Button>
            <div className="contact-note">
              Expert users: To create a carousel of designs for your booking{" "}
              <Link to={NextRouteURLs.CONTACT}>contact us</Link>.
            </div>
          </Form>
        )}
        {disabled && (
          <div className="submitting">
            <h2>Creating your design...</h2>
            <ProgressSpinner />
          </div>
        )}
      </Modal>
    );
  }
}
